import {Validators}                     from '@angular/forms';
import {Injectable}                     from '@angular/core';
import {ApDynformsConfigFieldset}       from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox
}                                       from '../../../../ap-dynforms/config/ap-dynforms-config-textbox';
import {nameOf}                         from '../../../../ap-core/utils/ap-name-of';
import {
  IFwPlanPlanningWizardDetailsData,
  IFwPlanPlanningWizardDetailsResponsiveData,
  WorkTypeCategory
}                                       from '../fw-plan-planning.types';
import {ApDynformsValidator}            from '../../../../ap-dynforms/ap-dynforms-validator';
import {
  ApDynformsConfigComboBox
}                                       from '../../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsConfigNumerictextbox} from '../../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {ApDynformsConfigTextarea}       from '../../../../ap-dynforms/config/ap-dynforms-config-textarea';
import {ApTranslationService}           from '../../../../ap-utils/service/ap-translation.service';
import {FwPlanPlanningCommonConfig}     from './fw-plan-planning-common.config';
import IUnit = Data.Common.IUnit;
import IWorktypes = Data.BaseData.IWorktypes;

@Injectable()
export class FwPlanPlanningDetailsConfig {
  constructor(private translationService: ApTranslationService,
              private fwPlanPlanningWizardCommonConfig: FwPlanPlanningCommonConfig) {
  }

  /**
   * Generates form fieldsets for the plan planning wizard details page
   * @param detailsData - Data object containing field counts, areas and work type details
   * @param responsiveData - Data object containing observables for work types, units and categories
   * @returns Array of fieldset configurations for the form containing:
   * - Information fieldset with count fields and total area
   * - General fieldset from common config
   * - Work information fieldset with work type, value and unit inputs
   * - Documentation fieldset from common config
   * @public
   */
  public generateFields(detailsData: IFwPlanPlanningWizardDetailsData,
                        responsiveData: IFwPlanPlanningWizardDetailsResponsiveData): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'Information',
        legend: 'Global__Information',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('CountFields'),
            label: 'AgriConnect__Count_Fields',
            informational: true,
            value: detailsData.CountFields
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('TotalArea'),
            label: 'Nutrients__TotalArea_ha',
            informational: true,
            value: detailsData.TotalArea
          })
        ]
      }),
      this.fwPlanPlanningWizardCommonConfig.getGeneralFieldSet(detailsData, detailsData.CountFields === 1, false),
      new ApDynformsConfigFieldset({
        key: 'WorkInformation',
        legend: 'Work_Information',
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('WorkTypeCategory'),
            label: 'Worktype_Category',
            options: responsiveData.WorkTypeCategories$,
            valueField: nameOf<WorkTypeCategory>('CategoryId'),
            textField: nameOf<WorkTypeCategory>('CategoryText'),
            value: detailsData.WorkTypeCategory,
            translate: true,
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('WorkType'),
            label: 'Docu_Ini__Type',
            valueField: nameOf<IWorktypes>('Id'),
            textField: nameOf<IWorktypes>('Description'),
            value: detailsData.WorkType,
            options: responsiveData.WorkTypes$,
            translate: true,
            valuePrimitive: true,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('Value'),
            label: 'Worktype_Value',
            value: detailsData.Value,
            disabled$: responsiveData.WorkTypeValueDisabled$
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('Unit'),
            label: 'Worktype_Unit',
            valueField: nameOf<IUnit>('Id'),
            textField: nameOf<IUnit>('Key'),
            value: detailsData.Unit,
            options: responsiveData.WorkTypeUnits$,
            disabled$: responsiveData.WorkTypeUnitsDisabled$,
            translate: true,
            valuePrimitive: true
          }),
          new ApDynformsConfigTextarea({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('Notes'),
            label: 'Global__Note',
            rows: 2
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'Docu',
        legend: 'Settings__Lbl_Menu_Resources',
        config: [
          ...this.fwPlanPlanningWizardCommonConfig.getResourcesConfigs(detailsData),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IFwPlanPlanningWizardDetailsData>('HaPerHour'),
            label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
            value: detailsData.HaPerHour,
            decimals: 1,
            min: 0,
            step: 0.1
          })
        ]
      })
    ];
  }
}
